import { supabase } from '../supabaseClient'
import { offlineDB } from './db'
import { getPendingActions, markSynced, markFailed, setIdMapping, resolveId, claimAction } from './syncQueue'
import { authHeaders } from '../utils/authFetch'

let isSyncing = false

export async function runSync() {
    if (isSyncing) return 
    isSyncing = true
    let syncedCount = 0

    try {
        const pending = await getPendingActions()

        for (const action of pending) {
            try {
                const claimed = await claimAction(action.localId)
                if (!claimed) continue

                switch (action.type) {
                    case 'create_order':
                        await syncCreateOrder(action)
                        break
                    case 'mark_item_ready':
                        await syncMarkItemReady(action)
                        break
                    case 'void_item':
                        await syncVoidItem(action)
                        break
                    case 'stock_count':
                        await syncStockCount(action)
                        break
                    case 'adjustment_request':
                        await syncAdjustmentRequest(action)
                        break
                    case 'update_status':
                        await syncUpdateStatus(action)
                        break
                    case 'cancel_order':
                        await syncCancelOrder(action)
                        break
                    case 'create_loyalty_account':
                        await syncCreateLoyaltyAccount(action)
                        break
                    case 'earn_points_offline':
                        await syncEarnPointsOffline(action)
                        break
                    case 'clock_in':
                        await syncClockIn(action)
                        break
                    case 'clock_out':
                        await syncClockOut(action)
                        break
                    case 'open_cash_drawer':
                        await syncOpenCashDrawer(action)
                        break
                    case 'cash_drawer_transaction':
                        await syncCashDrawerTransaction(action)
                        break
                    case 'close_cash_drawer':
                        await syncCloseCashDrawer(action)
                        break
                    case 'create_remake_request':
                        await syncCreateRemakeRequest(action);
                        break
                    case 'claim_remake_request': 
                        await syncClaimRemakeRequest(action); 
                        break
                    case 'release_remake_request':
                        await syncReleaseRemakeRequest(action);
                        break
                    case 'complete_remake_request':
                        await syncCompleteRemakeRequest(action);
                        break
                    default:
                        console.warn('Unknown sync action type:', action.type)
                }
                 await markSynced(action.localId)
                syncedCount++
            } catch (err) {
                console.error('Sync failed for action', action, err)
                await markFailed(action.localId, err.message)
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('kaperk:sync-error', {
                        detail: { type: action.type, message: err.message }
                    }))
                }
                break
                // Stop the whole run here. Actions are order-dependent
                // (e.g. mark_item_ready needs its order's create_order to
                // have succeeded first), so we don't want to skip ahead
                // and process something whose dependency just failed.

            }
        }
    } finally {
        isSyncing = false
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('kaperk:sync-complete', { detail: { syncedCount } }))
        }
    }
}

// ── Helpers to resolve a real ID

async function resolveOrderId(clientRef, fallbackOrderId = null) {
    if (clientRef) {
        const realId = await resolveId(`local_order_${clientRef}`)
        if (realId) return realId

        // Fallback: this order was created online (has a real order_id already)
        // and never went through syncCreateOrder, so idMap never got a mapping
        // for it. Look it up directly by client_ref and cache the mapping so
        // future actions in this same sync run resolve instantly.
        const { data, error } = await supabase
            .from('orders')
            .select('order_id')
            .eq('client_ref', clientRef)
            .maybeSingle()

        if (error) throw error
        if (data) {
            await setIdMapping(`local_order_${clientRef}`, data.order_id)
            return data.order_id
        }
    }

    // No usable clientRef (null/undefined, or no matching row yet) — fall
    // back to the real order_id captured when the action was queued.
    if (fallbackOrderId) return fallbackOrderId

    throw new Error(`Order not yet synced (missing clientRef and orderId)`)
}

async function resolveOrderItemId(localItemKey) {
    const realId = await resolveId(localItemKey)
    if (!realId) throw new Error(`Order item ${localItemKey} not yet synced`)
    return realId
}

// ── Action handlers ─────────────────────────────────────────────────

async function syncCreateOrder(action) {
    const { order, items, clientRef } = action.payload

    const resolvedUserId = order.user_id ? await resolveId(order.user_id) : null
    if (order.user_id && !resolvedUserId) throw new Error(`Customer ${order.user_id} not yet synced`)

    const resolvedRemakeId = order.remake_request_id ? await resolveId(order.remake_request_id) : null
    if (order.remake_request_id && !resolvedRemakeId) throw new Error('Remake request not yet synced — will retry.')
    
    const resolvedOriginalOrderId = order.original_order_id ? await resolveId(order.original_order_id) : null
    if (order.original_order_id && !resolvedOriginalOrderId) throw new Error('Original order not yet synced — will retry.')
        
    const { data: orderData, error } = await supabase
        .from('orders')
        .upsert([{ ...order, user_id: resolvedUserId, client_ref: clientRef, remake_request_id: resolvedRemakeId, original_order_id: resolvedOriginalOrderId, }], { onConflict: 'client_ref' },)
        .select()
        .single()

    if (error) throw error

    await setIdMapping(`local_order_${clientRef}`, orderData.order_id)

    for (const item of items) {
        const {
            localItemKey,
            variantObjects,
            modifierObjects,
            reservationRows,
            clientRef: itemClientRef,
            name,
            ...itemColumns
        } = item

        const { data: itemData, error: itemError } = await supabase
            .from('order_items')
            .upsert(
                [{ ...itemColumns, order_id: orderData.order_id, client_ref: itemClientRef }],
                { onConflict: 'client_ref' }
            )
            .select()
            .single()

        if (itemError) throw itemError

        await setIdMapping(localItemKey, itemData.order_item_id)

        if (variantObjects?.length) {
            const { error: vErr } = await supabase.from('order_item_variants').upsert(
                variantObjects.map(v => ({
                    order_item_id: itemData.order_item_id,
                    variant_id: v.variant_id,
                    variant_price: v.price || 0,
                })),
                { onConflict: 'order_item_id,variant_id' }
            )
            if (vErr) console.error(`Failed to sync variants for item ${itemData.order_item_id}:`, vErr.message)
        }

        if (modifierObjects?.length) {
            const { error: mErr } = await supabase.from('order_item_modifiers').upsert(
                modifierObjects.map(m => ({
                    order_item_id: itemData.order_item_id,
                    modifier_id: m.modifier_id,
                    modifier_price: m.price || 0,
                })),
                { onConflict: 'order_item_id,modifier_id' }
            )
            if (mErr) console.error(`Failed to sync modifiers for item ${itemData.order_item_id}:`, mErr.message)
        }

        if (reservationRows?.length) {
            const rows = reservationRows.map(r => ({
                ...r,
                order_id: orderData.order_id,
                order_item_id: itemData.order_item_id,
            }))
            const { error: resErr } = await supabase
                .schema('inventory')
                .from('stock_reservations')
                .upsert(rows, { onConflict: 'order_item_id,ingredient_id' })
            if (resErr) console.error(`Failed to sync reservations for item ${itemData.order_item_id}:`, resErr.message)
        }
        
    }

    if (action.payload.transactions?.length) {
        const { error: txError } = await supabase.from('order_transactions').insert(
            action.payload.transactions.map(t => ({ ...t, order_id: orderData.order_id }))
        )
        if (txError) throw txError
    }

    if (action.payload.appliedDiscount?.type === 'campaign_voucher' && resolvedUserId) {
        const { data: loyaltyRow } = await supabase
            .from('loyalty_accounts')
            .select('loyalty_id')
            .eq('user_id', resolvedUserId)
            .maybeSingle()

        if (loyaltyRow) {
            const { error: redemptionError } = await supabase
                .schema('marketing')
                .from('reward_redemptions')
                .insert([{
                    reward_id: action.payload.appliedDiscount.rewardId,
                    loyalty_id: loyaltyRow.loyalty_id,
                    user_id: resolvedUserId,
                    branch_id: orderData.branch_id,
                    redemption_status: 'completed',
                    reference_number: orderData.reference_number,
                    order_id: orderData.order_id,
                    completed_at: new Date().toISOString(),
                }])
            if (redemptionError) console.error('Failed to sync voucher redemption:', redemptionError.message)
        }
    }
}

async function syncOpenCashDrawer(action) {
    const { localSessionId, branchId, baristaId, openingAmount, openingDenominations, notes } = action.payload

    // Guard against duplicates: if the branch already has an open session
    // on the server (opened elsewhere before this device went offline),
    // adopt that one instead of inserting a second open drawer.
    const { data: existing, error: existingError } = await supabase
        .from('cash_drawer_sessions')
        .select('*')
        .eq('branch_id', branchId)
        .eq('status', 'open')
        .order('opened_at', { ascending: false })
        .limit(1)
        .maybeSingle()
    if (existingError) throw existingError

    if (existing) {
        await setIdMapping(localSessionId, existing.session_id)
        await offlineDB.cashDrawerSessionCache.put({ ...existing, branch_id: branchId })
        return
    }

    const { data, error } = await supabase
        .from('cash_drawer_sessions')
        .insert({
            branch_id: branchId,
            barista_id: baristaId,
            status: 'open',
            opening_amount: openingAmount,
            opening_denominations: openingDenominations,
            notes: notes || null,
        })
        .select()
        .single()
    if (error) throw error

    await setIdMapping(localSessionId, data.session_id)
    await offlineDB.cashDrawerSessionCache.put({ ...data, branch_id: branchId })
}

async function syncCashDrawerTransaction(action) {
    const { localTxId, sessionId, branchId, baristaId, type, amount, reason } = action.payload
    const realSessionId = await resolveId(sessionId)
    if (!realSessionId) throw new Error('Cash drawer session not yet synced — will retry.')

    const { data, error } = await supabase
        .from('cash_drawer_transactions')
        .insert({ session_id: realSessionId, branch_id: branchId, barista_id: baristaId, type, amount, reason })
        .select()
        .single()
    if (error) throw error
    await setIdMapping(localTxId, data.transaction_id)
}

async function syncCloseCashDrawer(action) {
    const { sessionId, branchId, baristaId, closingAmount, closingDenominations, closingNotes, expectedAmount, variance } = action.payload
    const realSessionId = await resolveId(sessionId)
    if (!realSessionId) throw new Error('Cash drawer session not yet synced — will retry.')

    const { error } = await supabase
        .from('cash_drawer_sessions')
        .update({
            status: 'closed',
            closed_by_barista_id: baristaId,
            closing_amount: closingAmount,
            closing_denominations: closingDenominations,
            closing_notes: closingNotes,
            expected_amount: expectedAmount,
            variance,
            closed_at: new Date().toISOString(),
        })
        .eq('session_id', realSessionId)
    if (error) throw error
}

async function syncCreateRemakeRequest(action) {
    const { localRequestId, branchId, orderId, referenceNumber, reason, responsibleStaff, remakeItems, submittedBy, submittedByBarista } = action.payload
    const realOrderId = await resolveId(orderId) // works whether orderId is real or "local_order_..."
    if (!realOrderId) throw new Error('Original order not yet synced — will retry.')

    const { data, error } = await supabase.from('remake_requests').insert([{
        order_id: realOrderId, reference_number: referenceNumber, branch_id: branchId, reason,
        responsible_staff: responsibleStaff, remake_items: remakeItems,
        submitted_by: submittedBy, submitted_by_barista: submittedByBarista, status: 'pending',
    }]).select().single()
    if (error) throw error
    await setIdMapping(localRequestId, data.remake_request_id)

    if (responsibleStaff.length) {
        const { error: actErr } = await supabase.from('shift_activity_log').insert(
            responsibleStaff.map(p => ({ barista_id: p.staff_id, branch_id: branchId, activity_type: 'remake_request', order_id: realOrderId, reference_number: referenceNumber, reason, remake_request_id: data.remake_request_id }))
        )
        if (actErr) console.error('Failed to sync remake activity log:', actErr.message)
    }
}

async function syncClaimRemakeRequest(action) {
    const realId = await resolveId(action.payload.remakeRequestId)
    if (!realId) throw new Error('Remake request not yet synced — will retry.')
    const { error } = await supabase.from('remake_requests').update({ status: 'in_progress', claimed_by: action.payload.claimantId }).eq('remake_request_id', realId)
    if (error) throw error
}

async function syncReleaseRemakeRequest(action) {
    const realId = await resolveId(action.payload.remakeRequestId)
    if (!realId) throw new Error('Remake request not yet synced — will retry.')
    const { error } = await supabase.from('remake_requests').update({ status: 'pending', claimed_by: null }).eq('remake_request_id', realId)
    if (error) throw error
}

async function syncCompleteRemakeRequest(action) {
    const realId = await resolveId(action.payload.remakeRequestId)
    if (!realId) throw new Error('Remake request not yet synced — will retry.')
    const { error } = await supabase.from('remake_requests').update({ status: 'completed', completed_at: new Date().toISOString() }).eq('remake_request_id', realId)
    if (error) throw error
}

async function syncCreateLoyaltyAccount(action) {
    const { customer, localCustomerKey, localLoyaltyKey } = action.payload

    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080'
    const res = await fetch(`${API_BASE_URL}/api/pos/loyalty/sync-offline-account`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ customer }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to sync loyalty account')

    await setIdMapping(localCustomerKey, data.userId)
    await setIdMapping(localLoyaltyKey, data.loyaltyId)

    // The placeholder cached under this email may have been wrong (e.g. it
    // guessed "new account" while offline, but the account actually already
    // existed). Overwrite it with the real, resolved record now that we know.
    const { data: realCustomer } = await supabase
        .from('customers').select('first_name, last_name').eq('user_id', data.userId).maybeSingle()
    const { data: realLoyalty } = await supabase
        .from('loyalty_accounts').select('membership_number, points_balance').eq('loyalty_id', data.loyaltyId).maybeSingle()

    if (realCustomer && realLoyalty) {
        await offlineDB.customerLoyaltyCache.put({
            email: customer.email,
            user_id: data.userId,
            loyalty_id: data.loyaltyId,
            membership_number: realLoyalty.membership_number,
            points_balance: Number(realLoyalty.points_balance || 0),
            name: `${realCustomer.first_name} ${realCustomer.last_name}`,
        })
    }

    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('kaperk:loyalty-synced', {
            detail: { email: customer.email, localLoyaltyKey, realLoyaltyId: data.loyaltyId, realUserId: data.userId }
        }))
    }
}

async function syncEarnPointsOffline(action) {
    const { loyaltyId, orderClientRef, pointsEarned, referenceNumber, totalAmount } = action.payload

    const realLoyaltyId = await resolveId(loyaltyId)
    if (!realLoyaltyId) throw new Error(`Loyalty account ${loyaltyId} not yet synced`)
    const orderId = await resolveOrderId(orderClientRef)

    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080'
    const res = await fetch(`${API_BASE_URL}/api/pos/loyalty/award-points`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
            loyaltyId: realLoyaltyId,
            orderId,
            pointsEarned,
            referenceNumber,
            totalAmount,
        }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to sync points')
}

async function syncMarkItemReady(action) {
    const { orderItemId, localItemKey, orderClientRef, orderId, staffId } = action.payload

    // Always resolve — resolveId() is a no-op for real (non "local_") ids,
    // so this is safe whether the item was created online or offline.
    const resolvedItemId = await resolveOrderItemId(orderItemId || localItemKey)
    if (orderClientRef || orderId) await resolveOrderId(orderClientRef, orderId)

    const { error } = await supabase
        .from('order_items')
        .update({ prep_status: 'ready', processed_by: staffId })
        .eq('order_item_id', resolvedItemId)

    if (error) throw error
}

async function syncUpdateStatus(action) {
    const { orderClientRef, orderId: fallbackOrderId, newStatus, completedBy } = action.payload
    const orderId = await resolveOrderId(orderClientRef, fallbackOrderId)
    const updateData = { order_status: newStatus, updated_at: new Date().toISOString() }
    if (newStatus === 'completed') {
        updateData.completed_at = new Date().toISOString()
        updateData.order_completed_by = completedBy ?? null
    }
    const { error } = await supabase.from('orders').update(updateData).eq('order_id', orderId)
    if (error) throw error
    if (newStatus === 'completed') {
        const { error: prepError } = await supabase
            .from('order_items')
            .update({ prep_status: 'completed' })
            .eq('order_id', orderId)
            .in('prep_status', ['pending', 'ready', 'preparing'])
            .not('line_status', 'in', '("voided","void_approved")')
        if (prepError) console.error('Failed to sync item completion:', prepError.message)

        const { error: rpcError } = await supabase.rpc('consume_reserved_stock', { p_order_id: orderId })
        if (rpcError) throw rpcError
    } else if (newStatus === 'preparing') {
        const { error: prepStartError } = await supabase
            .from('order_items')
            .update({ prep_status: 'preparing' })
            .eq('order_id', orderId)
            .eq('prep_status', 'pending')
            .not('line_status', 'in', '("voided","void_approved")')
        if (prepStartError) console.error('Failed to sync item preparing state:', prepStartError.message)
    }
}

async function syncVoidItem(action) {
    const { orderClientRef, localItemKey, reason } = action.payload

    if (orderClientRef) await resolveOrderId(orderClientRef)
    const orderItemId = await resolveOrderItemId(localItemKey)

    const { error } = await supabase
        .from('order_items')
        .update({ line_status: 'voided', void_reason: reason })
        .eq('order_item_id', orderItemId)

    if (error) throw error
}

async function syncStockCount(action) {
    const { branchId, ingredientId, countedQuantity, countType, staffRecordId, baristaId, unit } = action.payload
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080'
    const res = await fetch(`${API_BASE_URL}/api/inventory/${branchId}/stock-count`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ ingredientId, countedQuantity, countType, staffRecordId, baristaId, unit }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to sync stock count')
}

async function syncAdjustmentRequest(action) {
    const { branchId, ingredientId, adjustmentType, quantity, reason, notes, staffRecordId, baristaId } = action.payload
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080'
    const res = await fetch(`${API_BASE_URL}/api/inventory/${branchId}/adjustment-requests`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ ingredientId, adjustmentType, quantity, reason, notes, staffRecordId, baristaId }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to sync adjustment request')
}

async function syncCancelOrder(action) {
    const { orderClientRef, orderId: fallbackOrderId, branchId } = action.payload
    const orderId = await resolveOrderId(orderClientRef, fallbackOrderId)

    // Void all still-active items
    const { data: activeItems, error: fetchError } = await supabase
        .from('order_items')
        .select('order_item_id, prep_status')
        .eq('order_id', orderId)
        .eq('line_status', 'active')
    if (fetchError) throw fetchError

    if (activeItems?.length) {
        const { error: voidErr } = await supabase
            .from('order_items')
            .update({ line_status: 'voided', void_reason: 'order_cancelled' })
            .in('order_item_id', activeItems.map(i => i.order_item_id))
        if (voidErr) throw voidErr
    }

    // Release reservations for items that weren't already marked ready
    const notReadyIds = (activeItems || [])
        .filter(i => String(i.prep_status || '').toLowerCase() !== 'ready')
        .map(i => i.order_item_id)

    if (notReadyIds.length) {
        const { error: releaseErr } = await supabase
            .schema('inventory')
            .from('stock_reservations')
            .update({ status: 'returned' })
            .eq('order_id', orderId)
            .eq('branch_id', branchId)
            .eq('status', 'reserved')
            .in('order_item_id', notReadyIds)
        if (releaseErr) throw releaseErr
    }

    await supabase
        .from('order_transactions')
        .update({ status: 'cancelled', updated_at: new Date().toISOString() })
        .eq('order_id', orderId)
        .eq('status', 'pending')

    const { error: orderErr } = await supabase
        .from('orders')
        .update({ order_status: 'cancelled', updated_at: new Date().toISOString() })
        .eq('order_id', orderId)
        .eq('branch_id', branchId)

    if (orderErr) throw orderErr
}

// ── Attendance sync handlers ────────────────────────────────────────

function base64ToBlob(base64) {
    const [header, data] = base64.split(',')
    const mimeMatch = header.match(/:(.*?);/)
    const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg'
    const binary = atob(data)
    const array = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) array[i] = binary.charCodeAt(i)
    return new Blob([array], { type: mime })
}

async function uploadOfflinePhoto(photoBase64, branchId, ownerId, type, timestamp) {
    if (!photoBase64) return null
    const blob = base64ToBlob(photoBase64)
    const dateStr = new Date(timestamp).toISOString().slice(0, 10)
    const timeStr = new Date(timestamp).toISOString().slice(11, 19).replace(/:/g, '-')
    const filename = `${branchId}/${ownerId}_${type}_${dateStr}_${timeStr}.png`

    const { error: uploadError } = await supabase.storage
        .from('staff_attendance')
        .upload(filename, blob, { contentType: 'image/jpeg', upsert: true })
    if (uploadError) throw uploadError

    const { data: urlData } = supabase.storage.from('staff_attendance').getPublicUrl(filename)
    return urlData?.publicUrl ?? null
}

async function syncClockIn(action) {
    const { staffId, baristaId, branchId, date, clockIn, status, photoBase64, scheduledIn, scheduledOut, existingRecordId } = action.payload
    const ownerId = baristaId || staffId

    const photoUrl = await uploadOfflinePhoto(photoBase64, branchId, ownerId, 'clock_in', clockIn)

    if (existingRecordId) {
        const { error } = await supabase
            .from('staff_attendance')
            .update({ clock_in: clockIn, status, clock_in_photo: photoUrl })
            .eq('id', existingRecordId)
        if (error) throw error
        return
    }

    const { error: insertError } = await supabase
        .from('staff_attendance')
        .insert({
            staff_id: staffId,
            barista_id: baristaId,
            branch_id: branchId,
            date,
            clock_in: clockIn,
            status,
            clock_in_photo: photoUrl,
            scheduled_in: scheduledIn,
            scheduled_out: scheduledOut,
        })

    if (insertError?.code === '23505') {
        // Row for today already exists (e.g. it was created online while
        // this action sat queued). Update it instead of failing.
        const idCol = baristaId ? 'barista_id' : 'staff_id'
        const { error: updateErr } = await supabase
            .from('staff_attendance')
            .update({ clock_in: clockIn, status, clock_in_photo: photoUrl })
            .eq(idCol, ownerId)
            .eq('date', date)
        if (updateErr) throw updateErr
    } else if (insertError) {
        throw insertError
    }
}

async function syncClockOut(action) {
    const { recordId, staffId, baristaId, branchId, date, clockOut, photoBase64 } = action.payload
    const ownerId = baristaId || staffId

    const photoUrl = await uploadOfflinePhoto(photoBase64, branchId, ownerId, 'clock_out', clockOut)

    if (recordId) {
        const { error } = await supabase
            .from('staff_attendance')
            .update({ clock_out: clockOut, clock_out_photo: photoUrl })
            .eq('id', recordId)
        if (error) throw error
        return
    }

    // No real row id yet — the clock-in for today hadn't synced when this
    // was queued. Sync runs actions in order, so by now the clock_in action
    // above should have created the row; resolve it by owner + date.
    if (!ownerId || !date) {
        throw new Error('Clock out: missing identifying info to resolve attendance record')
    }
    const idCol = baristaId ? 'barista_id' : 'staff_id'
    const { error } = await supabase
        .from('staff_attendance')
        .update({ clock_out: clockOut, clock_out_photo: photoUrl })
        .eq(idCol, ownerId)
        .eq('date', date)
    if (error) throw error
}